import {
	CircleAlert,
	CircleCheck,
	CircleX,
	Eye,
	EyeClosed,
	Loader2,
	Lock,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getCsrfToken } from "@/lib/csrf";
import { cn, populateQueryParams } from "@/lib/utils";

export interface AccountCheckResult {
	found: boolean;
	verified: boolean;
	alreadyLinked: boolean;
	/** "rename" the current account, "relink" to the target account, null = blocked */
	path: "rename" | "relink" | null;
	/** only set on the relink path — renaming leaves no old account behind */
	oldAccountFate: "delete" | "suspend" | null;
}

export interface SwitchAccountContentProps {
	/** id of the therapist/admin profile whose login account is being fixed */
	profileId: string | number;
	/** email of the account currently attached to the profile */
	currentEmail: string;
	/** GET endpoint returning AccountCheckResult */
	checkUrl: string;
	/** PUT endpoint that performs the switch; Rails picks rename vs relink */
	switchUrl: string;
	onSuccess: () => void;
}

const Line = ({ ok, children }: { ok: boolean; children: React.ReactNode }) => (
	<li className="flex items-start gap-2">
		{ok ? (
			<CircleCheck className="mt-0.5 size-4 shrink-0 text-emerald-600" />
		) : (
			<CircleX className="mt-0.5 size-4 shrink-0 text-destructive" />
		)}
		<span className={cn(!ok && "text-destructive")}>{children}</span>
	</li>
);

export const SwitchAccountContent = ({
	profileId,
	currentEmail,
	checkUrl,
	switchUrl,
	onSuccess,
}: SwitchAccountContentProps) => {
	const { t } = useTranslation("translation", {
		keyPrefix: "components.switch_account",
	});
	const [email, setEmail] = useState("");
	const [isChecking, setIsChecking] = useState(false);
	const [error, setError] = useState("");
	const [result, setResult] = useState<AccountCheckResult | null>(null);
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [isMoving, setIsMoving] = useState(false);

	const check = async () => {
		const { fullUrl } = populateQueryParams(checkUrl, {
			profile_id: profileId,
			email,
		});

		setIsChecking(true);
		setError("");
		setResult(null);
		try {
			const res = await fetch(fullUrl);
			const data = await res.json();
			if (!res.ok) throw new Error(data?.error || t("check_failed"));
			setResult({
				found: data.found,
				verified: data.verified,
				alreadyLinked: data.already_linked,
				path: data.path,
				oldAccountFate: data.old_account_fate,
			});
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setIsChecking(false);
		}
	};

	// Which endpoint the switch goes through is Rails' call, not this dialog's.
	const move = async () => {
		setIsMoving(true);
		setError("");
		try {
			const res = await fetch(switchUrl, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": getCsrfToken(),
				},
				body: JSON.stringify({
					profile_id: profileId,
					email,
					confirm_password: password,
				}),
			});
			const data = await res.json();
			if (!res.ok || !data.success) {
				throw new Error(data?.message || t("move_failed"));
			}

			toast.success(data.message);
			onSuccess();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setIsMoving(false);
		}
	};

	return (
		<div className="grid gap-4">
			<div className="grid gap-1.5">
				<Label>{t("current_account")}</Label>
				<Input readOnly value={currentEmail} />
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="target-email">{t("destination")}</Label>
				<div className="flex gap-2">
					<Input
						id="target-email"
						type="email"
						placeholder={t("destination_placeholder")}
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							// the step-up belongs to the destination that was checked
							setResult(null);
							setPassword("");
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" && email) check();
						}}
					/>
					<Button
						type="button"
						variant="secondary"
						disabled={!email || isChecking}
						onClick={check}
					>
						{isChecking && <Loader2 className="animate-spin" />}
						{t("check")}
					</Button>
				</div>
				<p className="text-xs text-muted-foreground">{t("hint")}</p>
			</div>

			{error && (
				<p className="flex items-start gap-2 text-sm text-destructive">
					<CircleAlert className="mt-0.5 size-4 shrink-0" />
					{error}
				</p>
			)}

			{result && (
				<ul className="grid gap-2 p-3 text-sm border rounded-md bg-muted/40">
					{result.found ? (
						<>
							<Line ok>{t("found")}</Line>
							<Line ok={result.verified}>
								{result.verified ? t("verified") : t("unverified")}
							</Line>
							<Line ok={!result.alreadyLinked}>
								{result.alreadyLinked ? t("linked") : t("unlinked")}
							</Line>
						</>
					) : (
						<Line ok>{t("rename")}</Line>
					)}

					{result.path === "relink" && (
						<li className="flex items-start gap-2 pt-2 border-t">
							<CircleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
							<span>
								{t("relink", { email: currentEmail })}{" "}
								<b>{t(`fate.${result.oldAccountFate ?? "suspend"}`)}</b>.
							</span>
						</li>
					)}
				</ul>
			)}

			{/* Step-up only appears once the consequences above have been rendered. */}
			{result?.path && (
				<div className="grid gap-1.5">
					<Label htmlFor="confirm-password">{t("password")}</Label>
					<Input
						id="confirm-password"
						type={showPassword ? "text" : "password"}
						placeholder={t("password_placeholder")}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						StartIcon={{ icon: Lock }}
						EndIcon={{
							icon: showPassword ? EyeClosed : Eye,
							isButton: true,
							handleOnClick: () => setShowPassword((prev) => !prev),
						}}
						disabled={isMoving}
					/>

					<Button
						type="button"
						className="mt-2"
						disabled={!password || isMoving}
						onClick={move}
					>
						{isMoving && <Loader2 className="animate-spin" />}
						{t("move")}
					</Button>
				</div>
			)}
		</div>
	);
};
